import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import QRCode from "qrcode";
import {
  PawPrint,
  QrCode,
  ShoppingCart,
  Calendar,
  MapPin,
  User,
  Phone,
  Mail,
  Download,
  X,
  Plus,
  Loader,
  FileText,
  Trash2,
} from "lucide-react";

const MyPetsPage = ({ user }) => {
  const { themeClasses } = useTheme();
  const navigate = useNavigate();
  const [userPets, setUserPets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize Firebase Functions
  const functions = getFunctions(app);

  // QR Code Modal states (matching original)
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [qrCodeDataURL, setQrCodeDataURL] = useState("");

  // Pet Tag Order Modal states (matching original)
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderData, setOrderData] = useState({
    petId: "",
    petName: "",
    tagType: "basic",
    quantity: 1,
    paymentMethod: "card",
    shippingAddress: {
      fullName: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "United States",
    },
    cardInfo: {
      cardholderName: "",
      cardNumber: "",
      expiryDate: "",
      cvv: "",
    },
  });
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [deletingPetId, setDeletingPetId] = useState(null);

  useEffect(() => {
    if (user) {
      loadUserPets();
    }
  }, [user]);

  const loadUserPets = async () => {
    if (!user?.uid) return;

    setLoading(true);
    console.log("Loading pets for user:", user.uid);

    try {
      const getUserPets = httpsCallable(functions, "getUserPets");
      const result = await getUserPets();

      if (result.data.success) {
        const pets = result.data.pets.map((pet) => {
          let createdAt = null;

          // Debug: Log the original createdAt format
          console.log(
            "Original createdAt for",
            pet.name,
            ":",
            pet.createdAt,
            typeof pet.createdAt
          );

          if (pet.createdAt) {
            // Handle Firestore Timestamp objects
            if (
              pet.createdAt.toDate &&
              typeof pet.createdAt.toDate === "function"
            ) {
              createdAt = pet.createdAt.toDate();
            }
            // Handle timestamp objects with seconds/nanoseconds
            else if (pet.createdAt._seconds || pet.createdAt.seconds) {
              const seconds = pet.createdAt._seconds || pet.createdAt.seconds;
              createdAt = new Date(seconds * 1000);
            }
            // Handle ISO string or timestamp number
            else if (
              typeof pet.createdAt === "string" ||
              typeof pet.createdAt === "number"
            ) {
              createdAt = new Date(pet.createdAt);
            }
          }

          return {
            ...pet,
            createdAt:
              createdAt && !isNaN(createdAt.getTime()) ? createdAt : null,
          };
        });

        // Sort by creation date (newest first)
        pets.sort((a, b) => {
          const aDate =
            a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const bDate =
            b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return bDate - aDate;
        });

        console.log("Firebase function - Setting userPets:", pets);
        setUserPets(pets);
      } else {
        console.error("Failed to load pets:", result.data.message);
        setUserPets([]);
      }
    } catch (error) {
      console.error("Failed to load user pets:", error);
      setUserPets([]);
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (pet) => {
    try {
      setSelectedPet(pet);

      // Create QR code data with pet information and profile URL
      const qrData = {
        petId: pet.id,
        name: pet.name,
        species: pet.species,
        breed: pet.breed,
        contact: pet.contactInfo?.phone || "Contact owner via app",
        profileUrl: `https://petid-77a78.web.app/pet/${pet.id}`,
        message: `Found ${pet.name}? Please contact the owner!`,
      };

      // Generate QR code as data URL
      const qrCodeURL = await QRCode.toDataURL(JSON.stringify(qrData), {
        width: 300,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });

      setQrCodeDataURL(qrCodeURL);
      setShowQRModal(true);
    } catch (error) {
      console.error("Failed to generate QR code:", error);
      alert("Failed to generate QR code: " + error.message);
    }
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setSelectedPet(null);
    setQrCodeDataURL("");
  };

  const downloadQRCode = () => {
    if (!qrCodeDataURL || !selectedPet) return;

    const link = document.createElement("a");
    link.download = `${selectedPet.name}-QR-Code.png`;
    link.href = qrCodeDataURL;
    link.click();
  };

  const openOrderModal = (pet) => {
    setOrderData((prev) => ({
      ...prev,
      petId: pet.id,
      petName: pet.name,
      shippingAddress: {
        ...prev.shippingAddress,
        fullName: user.displayName || "",
      },
    }));
    setShowOrderModal(true);
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setOrderData((prev) => ({
      ...prev,
      petId: "",
      petName: "",
    }));
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    setIsProcessingOrder(true);

    try {
      // Simulate order processing
      await new Promise((resolve) => setTimeout(resolve, 2000));

      alert(
        `🎉 Order placed successfully!\n\nPet Tag for ${
          orderData.petName
        }\nTotal: $${
          orderData.tagType === "premium" ? "25.00" : "15.00"
        }\n\nYour custom pet tag will be shipped to:\n${
          orderData.shippingAddress.address1
        }\n${orderData.shippingAddress.city}, ${
          orderData.shippingAddress.state
        } ${
          orderData.shippingAddress.zipCode
        }\n\nExpected delivery: 5-7 business days`
      );

      closeOrderModal();
    } catch (error) {
      console.error("Order processing failed:", error);
      alert("Failed to process order. Please try again.");
    } finally {
      setIsProcessingOrder(false);
    }
  };

  const updateOrderData = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setOrderData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setOrderData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const deletePet = async (pet) => {
    // Double confirmation for safety
    const confirmMessage = `Are you sure you want to delete ${pet.name}?\n\nThis will permanently delete:\n• Pet profile and information\n• All health records\n• All vaccination records\n• All uploaded photos\n• All related storage files\n\nThis action cannot be undone!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Second confirmation with name typing
    const userInput = window.prompt(
      `Type "${pet.name}" to confirm deletion (case sensitive):`
    );
    if (userInput !== pet.name) {
      alert("Pet name doesn't match. Deletion cancelled.");
      return;
    }

    try {
      setDeletingPetId(pet.id);

      const deletePetFn = httpsCallable(functions, "deletePet");
      const result = await deletePetFn({
        petId: pet.id,
        confirmDelete: true,
      });

      if (result.data.success) {
        // Show success message with details
        const deletedInfo = result.data.deletedRecords;
        alert(
          `${pet.name} has been successfully deleted!\n\nDeleted:\n• Pet profile\n• ${deletedInfo.healthRecords} health records\n• ${deletedInfo.vaccinations} vaccination records\n• ${deletedInfo.storageFiles} storage files`
        );

        // Reload the pets list
        await loadUserPets();
      } else {
        throw new Error(result.data.message || "Failed to delete pet");
      }
    } catch (error) {
      console.error("Error deleting pet:", error);
      alert("Failed to delete pet: " + error.message);
    } finally {
      setDeletingPetId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader
          className={`w-8 h-8 ${themeClasses.text.accent} animate-spin`}
        />
        <span className={`ml-3 ${themeClasses.text.secondary}`}>
          Loading your pets...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2
          className={`text-2xl font-bold ${themeClasses.text.primary} flex items-center`}
        >
          <PawPrint className={`w-8 h-8 ${themeClasses.text.accent} mr-3`} />
          My Pets ({userPets.length})
        </h2>
        <button
          onClick={() => navigate("/dashboard/register")}
          className={`flex items-center space-x-2 ${themeClasses.bg.button} text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300`}
        >
          <Plus className="w-4 h-4" />
          <span>Add Pet</span>
        </button>
      </div>

      {userPets.length === 0 ? (
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-12 text-center`}
        >
          <PawPrint
            className={`w-16 h-16 ${themeClasses.text.tertiary} mx-auto mb-4`}
          />
          <h3 className={`text-xl font-bold ${themeClasses.text.primary} mb-2`}>
            No Pets Registered Yet
          </h3>
          <p className={`${themeClasses.text.secondary} mb-6`}>
            Start by registering your first pet with AI breed detection!
          </p>
          <button
            onClick={() => navigate("/dashboard/register")}
            className={`${themeClasses.bg.button} text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300`}
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            Register Your First Pet
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {userPets.map((pet) => (
            <div
              key={pet.id}
              className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6 hover:scale-105 transition-transform`}
            >
              <div className="text-center">
                <div
                  className={`w-28 h-28 rounded-full overflow-hidden mx-auto mb-4 border-2 ${themeClasses.border.accent}`}
                >
                  {(() => {
                    // PRIORITY 1: Use instant-loading thumbnail for avatar (new feature!)
                    if (pet.avatarThumbnail) {
                      return (
                        <img
                          src={pet.avatarThumbnail}
                          alt={pet.name}
                          className="w-full h-full object-cover"
                          style={{ imageRendering: "auto" }} // Smooth scaling for small thumbnails
                        />
                      );
                    }

                    // PRIORITY 2: Handle new format with photos object (frontFace, leftSide, rightSide, etc.)
                    if (
                      pet.photos &&
                      typeof pet.photos === "object" &&
                      !Array.isArray(pet.photos)
                    ) {
                      const frontFacePhoto = pet.photos.frontFace;
                      if (frontFacePhoto && frontFacePhoto.url) {
                        return (
                          <img
                            src={frontFacePhoto.url}
                            alt={pet.name}
                            className="w-full h-full object-cover"
                          />
                        );
                      }
                      // Try other photo types if frontFace is not available
                      const photoTypes = ["leftSide", "rightSide", "fullBody"];
                      for (const type of photoTypes) {
                        if (pet.photos[type] && pet.photos[type].url) {
                          return (
                            <img
                              src={pet.photos[type].url}
                              alt={pet.name}
                              className="w-full h-full object-cover"
                            />
                          );
                        }
                      }
                    }

                    // Handle old format with photos array
                    if (
                      pet.photos &&
                      Array.isArray(pet.photos) &&
                      pet.photos.length > 0
                    ) {
                      const photo = pet.photos[0];
                      let imageSrc;

                      if (photo.startsWith("data:")) {
                        // Already a data URL
                        imageSrc = photo;
                      } else if (photo.startsWith("http")) {
                        // Firebase Storage URL or other HTTP URL
                        imageSrc = photo;
                      } else {
                        // Assume it's base64 string
                        imageSrc = `data:image/jpeg;base64,${photo}`;
                      }

                      return (
                        <img
                          src={imageSrc}
                          alt={pet.name}
                          className="w-full h-full object-cover"
                        />
                      );
                    }

                    // Handle imageUrl format
                    if (pet.imageUrl) {
                      return (
                        <img
                          src={pet.imageUrl}
                          alt={pet.name}
                          className="w-full h-full object-cover"
                        />
                      );
                    }

                    // Fallback to placeholder
                    return (
                      <div
                        className={`w-full h-full ${themeClasses.bg.tertiary} flex items-center justify-center`}
                      >
                        <User
                          className={`w-12 h-12 ${themeClasses.text.tertiary}`}
                        />
                      </div>
                    );
                  })()}
                </div>
                <h3
                  className={`text-lg font-bold ${themeClasses.text.primary} mb-1`}
                >
                  {pet.name}
                </h3>
                <p className={`${themeClasses.text.secondary} mb-1`}>
                  {pet.species} • {pet.breed}
                  {pet.breedConfidence > 0 && (
                    <span
                      className={`${themeClasses.text.success} text-xs ml-1`}
                    >
                      (AI: {Math.round(pet.breedConfidence * 100)}%)
                    </span>
                  )}
                </p>
                <p className={`${themeClasses.text.secondary} mb-2`}>
                  {pet.age > 0 ? `Age: ${pet.age} years` : "Age: Unknown"}
                </p>

                {/* Unique Pet ID */}
                <div
                  className={`bg-gradient-to-r from-cyan-500/10 to-purple-600/10 rounded-lg p-2 mb-3 border ${themeClasses.border.accent}`}
                >
                  <p
                    className={`text-sm ${themeClasses.text.accent} font-mono`}
                  >
                    ID: {pet.id}
                  </p>
                </div>

                {/* Description if available */}
                {pet.description && (
                  <p
                    className={`text-sm ${themeClasses.text.tertiary} mb-3 line-clamp-2`}
                  >
                    {pet.description}
                  </p>
                )}

                {/* Registration Date */}
                <p className={`text-xs ${themeClasses.text.tertiary} mb-4`}>
                  Registered:{" "}
                  {pet.createdAt && pet.createdAt instanceof Date
                    ? pet.createdAt.toLocaleDateString()
                    : "Unknown"}
                </p>

                <div className="flex justify-center space-x-2">
                  <button
                    title="Generate QR Code"
                    onClick={() => generateQRCode(pet)}
                    className={`p-2 ${themeClasses.bg.secondary} hover:${themeClasses.bg.tertiary} rounded-lg ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
                  >
                    <QrCode className="w-4 h-4" />
                  </button>
                  <button
                    title="Buy Pet Tag - $25"
                    onClick={() => openOrderModal(pet)}
                    className="p-2 bg-green-600 hover:bg-green-500 rounded-lg text-white transition-colors"
                  >
                    <ShoppingCart className="w-4 h-4" />
                  </button>
                  <button
                    title="Update Profile"
                    className={`p-2 ${themeClasses.bg.button} rounded-lg text-white transition-colors`}
                  >
                    <User className="w-4 h-4" />
                  </button>
                  <button
                    title="Delete Pet"
                    onClick={() => deletePet(pet)}
                    disabled={deletingPetId === pet.id}
                    className={`p-2 bg-red-600 hover:bg-red-700 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      deletingPetId === pet.id ? "animate-pulse" : ""
                    }`}
                  >
                    {deletingPetId === pet.id ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedPet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`${themeClasses.bg.modal} rounded-lg p-6 max-w-md w-full mx-4 border ${themeClasses.border.primary}`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-xl font-bold ${themeClasses.text.primary}`}>
                QR Code for {selectedPet.name}
              </h3>
              <button
                onClick={closeQRModal}
                className={`${themeClasses.text.tertiary} hover:${themeClasses.text.primary}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center">
              {qrCodeDataURL && (
                <div className="mb-4">
                  <img
                    src={qrCodeDataURL}
                    alt={`QR Code for ${selectedPet.name}`}
                    className="mx-auto bg-white p-4 rounded-lg"
                  />
                </div>
              )}

              <div
                className={`${themeClasses.bg.secondary} rounded-lg p-4 mb-4`}
              >
                <p className={`${themeClasses.text.secondary} text-sm mb-2`}>
                  This QR code contains:
                </p>
                <ul
                  className={`${themeClasses.text.tertiary} text-xs space-y-1`}
                >
                  <li>• Pet ID: {selectedPet.id}</li>
                  <li>• Name: {selectedPet.name}</li>
                  <li>• Species: {selectedPet.species}</li>
                  <li>• Breed: {selectedPet.breed}</li>
                  <li>• Profile URL</li>
                  <li>• Contact information</li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={downloadQRCode}
                  className={`flex-1 ${themeClasses.bg.button} text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300`}
                >
                  Download QR Code
                </button>
                <button
                  onClick={closeQRModal}
                  className={`flex-1 ${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} font-semibold py-2 px-4 rounded-lg border ${themeClasses.border.primary} transition-colors`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pet Tag Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className={`${themeClasses.bg.modal} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border ${themeClasses.border.primary}`}
          >
            <form onSubmit={handleOrderSubmit}>
              {/* Header */}
              <div
                className={`flex justify-between items-center p-6 border-b ${themeClasses.border.primary}`}
              >
                <h3
                  className={`text-2xl font-bold ${themeClasses.text.primary} flex items-center`}
                >
                  <FileText
                    className={`w-6 h-6 ${themeClasses.text.success} mr-2`}
                  />
                  Order Pet Tag for {orderData.petName}
                </h3>
                <button
                  type="button"
                  onClick={closeOrderModal}
                  className={`${themeClasses.text.tertiary} hover:${themeClasses.text.primary}`}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Tag Type Selection */}
                <div>
                  <h4
                    className={`text-lg font-bold ${themeClasses.text.primary} mb-4`}
                  >
                    Choose Tag Type
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        orderData.tagType === "basic"
                          ? `${themeClasses.border.accent} bg-cyan-500/10`
                          : `${themeClasses.border.secondary} hover:${themeClasses.border.primary}`
                      }`}
                      onClick={() => updateOrderData("tagType", "basic")}
                    >
                      <div className="text-center">
                        <div
                          className={`text-xl font-bold ${themeClasses.text.primary} mb-2`}
                        >
                          Basic Tag
                        </div>
                        <div
                          className={`text-2xl font-bold ${themeClasses.text.accent} mb-2`}
                        >
                          $15.00
                        </div>
                        <ul
                          className={`text-sm ${themeClasses.text.secondary} space-y-1`}
                        >
                          <li>• Durable aluminum</li>
                          <li>• Laser engraved</li>
                          <li>• Pet name & phone</li>
                          <li>• QR code</li>
                        </ul>
                      </div>
                    </div>

                    <div
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        orderData.tagType === "premium"
                          ? "border-purple-500 bg-purple-500/10"
                          : `${themeClasses.border.secondary} hover:${themeClasses.border.primary}`
                      }`}
                      onClick={() => updateOrderData("tagType", "premium")}
                    >
                      <div className="text-center">
                        <div
                          className={`text-xl font-bold ${themeClasses.text.primary} mb-2`}
                        >
                          Premium Tag
                        </div>
                        <div className="text-2xl font-bold text-purple-400 mb-2">
                          $25.00
                        </div>
                        <div className="bg-gradient-to-r from-purple-500/20 to-cyan-500/20 text-xs px-2 py-1 rounded mb-2">
                          RECOMMENDED
                        </div>
                        <ul
                          className={`text-sm ${themeClasses.text.secondary} space-y-1`}
                        >
                          <li>• Stainless steel</li>
                          <li>• Color QR code</li>
                          <li>• Pet photo engraving</li>
                          <li>• Medical info</li>
                          <li>• Waterproof coating</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <label
                    className={`block ${themeClasses.text.primary} font-medium mb-2`}
                  >
                    Quantity
                  </label>
                  <select
                    value={orderData.quantity}
                    onChange={(e) =>
                      updateOrderData("quantity", parseInt(e.target.value))
                    }
                    className={`w-full ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg px-3 py-2 ${themeClasses.text.primary} focus:outline-none focus:${themeClasses.border.accent}`}
                  >
                    <option value={1}>1 Tag</option>
                    <option value={2}>2 Tags</option>
                    <option value={3}>3 Tags</option>
                    <option value={4}>4 Tags</option>
                  </select>
                </div>

                {/* Shipping Address */}
                <div>
                  <h4
                    className={`text-lg font-bold ${themeClasses.text.primary} mb-4`}
                  >
                    Shipping Address
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        className={`block ${themeClasses.text.secondary} text-sm mb-1`}
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={orderData.shippingAddress.fullName}
                        onChange={(e) =>
                          updateOrderData(
                            "shippingAddress.fullName",
                            e.target.value
                          )
                        }
                        className={`w-full ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg px-3 py-2 ${themeClasses.text.primary} focus:outline-none focus:${themeClasses.border.accent}`}
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label
                        className={`block ${themeClasses.text.secondary} text-sm mb-1`}
                      >
                        Address Line 1 *
                      </label>
                      <input
                        type="text"
                        required
                        value={orderData.shippingAddress.address1}
                        onChange={(e) =>
                          updateOrderData(
                            "shippingAddress.address1",
                            e.target.value
                          )
                        }
                        className={`w-full ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg px-3 py-2 ${themeClasses.text.primary} focus:outline-none focus:${themeClasses.border.accent}`}
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div>
                      <label
                        className={`block ${themeClasses.text.secondary} text-sm mb-1`}
                      >
                        City *
                      </label>
                      <input
                        type="text"
                        required
                        value={orderData.shippingAddress.city}
                        onChange={(e) =>
                          updateOrderData(
                            "shippingAddress.city",
                            e.target.value
                          )
                        }
                        className={`w-full ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg px-3 py-2 ${themeClasses.text.primary} focus:outline-none focus:${themeClasses.border.accent}`}
                        placeholder="New York"
                      />
                    </div>
                    <div>
                      <label
                        className={`block ${themeClasses.text.secondary} text-sm mb-1`}
                      >
                        State *
                      </label>
                      <input
                        type="text"
                        required
                        value={orderData.shippingAddress.state}
                        onChange={(e) =>
                          updateOrderData(
                            "shippingAddress.state",
                            e.target.value
                          )
                        }
                        className={`w-full ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg px-3 py-2 ${themeClasses.text.primary} focus:outline-none focus:${themeClasses.border.accent}`}
                        placeholder="NY"
                      />
                    </div>
                    <div>
                      <label
                        className={`block ${themeClasses.text.secondary} text-sm mb-1`}
                      >
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        required
                        value={orderData.shippingAddress.zipCode}
                        onChange={(e) =>
                          updateOrderData(
                            "shippingAddress.zipCode",
                            e.target.value
                          )
                        }
                        className={`w-full ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg px-3 py-2 ${themeClasses.text.primary} focus:outline-none focus:${themeClasses.border.accent}`}
                        placeholder="10001"
                      />
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className={`${themeClasses.bg.secondary} rounded-lg p-4`}>
                  <h4
                    className={`text-lg font-bold ${themeClasses.text.primary} mb-3`}
                  >
                    Order Summary
                  </h4>
                  <div className="space-y-2">
                    <div
                      className={`flex justify-between ${themeClasses.text.secondary}`}
                    >
                      <span>
                        {orderData.tagType === "premium" ? "Premium" : "Basic"}{" "}
                        Pet Tag × {orderData.quantity}
                      </span>
                      <span>
                        $
                        {(orderData.tagType === "premium" ? 25 : 15) *
                          orderData.quantity}
                        .00
                      </span>
                    </div>
                    <div
                      className={`flex justify-between ${themeClasses.text.secondary}`}
                    >
                      <span>Shipping</span>
                      <span>FREE</span>
                    </div>
                    <div
                      className={`border-t ${themeClasses.border.primary} pt-2`}
                    >
                      <div
                        className={`flex justify-between ${themeClasses.text.primary} font-bold text-lg`}
                      >
                        <span>Total</span>
                        <span>
                          $
                          {(orderData.tagType === "premium" ? 25 : 15) *
                            orderData.quantity}
                          .00
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className={`flex justify-end space-x-3 p-6 border-t ${themeClasses.border.primary}`}
              >
                <button
                  type="button"
                  onClick={closeOrderModal}
                  className={`px-6 py-2 ${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} font-semibold rounded-lg border ${themeClasses.border.primary} transition-colors`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessingOrder}
                  className={`px-6 py-2 ${themeClasses.bg.button} disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-300 flex items-center`}
                >
                  {isProcessingOrder ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Place Order - $
                      {(orderData.tagType === "premium" ? 25 : 15) *
                        orderData.quantity}
                      .00
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPetsPage;
